import {
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
  ScmAuth,
} from '@backstage/integration-react';
import {
  AnyApiFactory,
  analyticsApiRef,
  configApiRef,
  identityApiRef,
  discoveryApiRef,
  fetchApiRef,
  createApiFactory,
  createApiRef,
  oauthRequestApiRef,
  type OAuthApi,
  type ProfileInfoApi,
  type BackstageIdentityApi,
  type SessionApi,
} from '@backstage/core-plugin-api';
import { OAuth2 } from '@backstage/core-app-api';
import { searchApiRef } from '@backstage/plugin-search-react';
import { ResponseError } from '@backstage/errors';
import qs from 'qs';
// ISDP CUSTOM: Tech Radar with custom data + Google Analytics 4 integration
import { GoogleAnalytics4 } from '@backstage-community/plugin-analytics-module-ga4';
import type { AnalyticsApi } from '@backstage/core-plugin-api';
import { UsageAnalyticsApi, CompositeAnalyticsApi } from './analytics/UsageAnalyticsApi';

// ISDP CUSTOM: client-side handle for the Okta/OIDC provider (DevSecOps Hub a0057 app), used by the
// sign-in screen so DevHub logs you in as your real Dräger identity (replacing guest).
export const oidcAuthApiRef = createApiRef<
  OAuthApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
>({ id: 'auth.draeger-oidc' });

export const apis: AnyApiFactory[] = [
  // ISDP CUSTOM: the Search API, registered explicitly.
  //
  // `searchApiRef` is normally implemented by @backstage/plugin-search's own api factory,
  // which only registers when something from that plugin is in the element tree. Removing
  // the unused /search PAGE in 00a7e79 removed the last reference to the plugin, so the
  // factory stopped registering and `useApi(searchApiRef)` threw NotImplementedError —
  // crashing the Home page (HomeSearch) for every signed-in user. The page was genuinely
  // dead; the API behind it is not. HomeSearch and useZeroResultSuggestions both use it.
  //
  // This mirrors upstream SearchClient exactly (qs.stringify of the query onto
  // GET /api/search/query) because SearchClient itself is not publicly exported.
  // Do not "clean this up" by deleting it unless nothing calls useApi(searchApiRef).
  createApiFactory({
    api: searchApiRef,
    deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
    factory: ({ discoveryApi, fetchApi }) => ({
      async query(query, options) {
        const url = `${await discoveryApi.getBaseUrl('search')}/query?${qs.stringify(
          query,
        )}`;
        const response = await fetchApi.fetch(url, { signal: options?.signal });
        if (!response.ok) {
          throw await ResponseError.fromResponse(response);
        }
        return await response.json();
      },
    }),
  }),
  // ISDP CUSTOM: Okta/OIDC sign-in provider (provider id 'oidc' on the backend).
  createApiFactory({
    api: oidcAuthApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      oauthRequestApi: oauthRequestApiRef,
      configApi: configApiRef,
    },
    factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
      OAuth2.create({
        configApi,
        discoveryApi,
        oauthRequestApi,
        provider: { id: 'oidc', title: 'Dräger', icon: () => null },
        environment: configApi.getOptionalString('auth.environment') ?? 'development',
        defaultScopes: ['openid', 'profile', 'email'],
      }),
  }),
  // ISDP CUSTOM: Tech radar — loads curated entries from the tech-radar backend

  createApiFactory({
    api: scmIntegrationsApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
  }),
  ScmAuth.createDefaultApiFactory(),
  // ISDP CUSTOM: analytics fan-out — Google Analytics 4 (page views/plugin usage)
  // PLUS the first-party anonymous usage tracker feeding the admin dashboard.
  createApiFactory({
    api: analyticsApiRef,
    deps: { configApi: configApiRef, identityApi: identityApiRef, fetchApi: fetchApiRef },
    factory: ({ configApi, identityApi, fetchApi }) => {
      const delegates: AnalyticsApi[] = [];
      try {
        delegates.push(GoogleAnalytics4.fromConfig(configApi, { identityApi }));
      } catch {
        /* GA4 not configured (e.g. dev) — skip it, keep first-party tracking */
      }
      delegates.push(new UsageAnalyticsApi(fetchApi, configApi));
      return new CompositeAnalyticsApi(delegates);
    },
  }),
];
